import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaTasks, FaChartPie, FaRobot, FaClock, FaStar, FaChevronLeft, FaChevronRight, FaPlus, FaEnvelope, FaPhone, FaMapMarkerAlt, FaLinkedin, FaTwitter, FaGithub, FaCheckCircle, FaSpinner, FaTimes } from 'react-icons/fa';
import apiService from '../services/api';

const LandingPage = () => {
    const [currentReviewIndex, setCurrentReviewIndex] = useState(0);
    const [currentPage, setCurrentPage] = useState('home');
    const [reviews, setReviews] = useState([]);
    const [showContactForm, setShowContactForm] = useState(false);
    const [showReviewForm, setShowReviewForm] = useState(false);
    const [contactSubmitting, setContactSubmitting] = useState(false);
    const [reviewSubmitting, setReviewSubmitting] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');

    // Contact form state
    const [contactForm, setContactForm] = useState({
        first_name: '',
        last_name: '',
        email: '',
        company: '',
        subject: '',
        message: ''
    });

    // Review form state
    const [reviewForm, setReviewForm] = useState({
        name: '',
        email: '',
        rating: 5,
        review: ''
    });

    // Fetch reviews on component mount
    useEffect(() => {
        fetchReviews();
    }, []);

    const fetchReviews = async () => {
        try {
            const data = await apiService.getPublicReviews();
            setReviews(data.length > 0 ? data : [
                // Fallback reviews if none exist
                {
                    id: '1',
                    name: "Sarah Johnson",
                    rating: 5,
                    review: "This task prioritization has completely transformed how our team manages projects. The intelligent analysis saves us hours.",
                    created_at: new Date().toISOString(),
                    is_verified: true
                },
                {
                    id: '2',
                    name: "Michael Chen",
                    rating: 4,
                    review: "The bulk CSV processing feature is incredible. We uploaded our entire backlog and got instant prioritization insights.",
                    created_at: new Date().toISOString(),
                    is_verified: true
                }
            ]);
        } catch (error) {
            console.error('Error fetching reviews:', error);
            // Use fallback reviews on error
            setReviews([
                {
                    id: '1',
                    name: "Demo User",
                    rating: 5,
                    review: "Great tool for managing tasks efficiently!",
                    created_at: new Date().toISOString(),
                    is_verified: false
                }
            ]);
        }
    };

    // Auto-slide functionality
    useEffect(() => {
        if (reviews.length === 0) return;

        const interval = setInterval(() => {
            setCurrentReviewIndex((prevIndex) => {
                const maxIndex = Math.ceil(reviews.length / 3) - 1;
                return prevIndex === maxIndex ? 0 : prevIndex + 1;
            });
        }, 6000);

        return () => clearInterval(interval);
    }, [reviews.length]);

    const nextReview = () => {
        const maxIndex = Math.ceil(reviews.length / 3) - 1;
        setCurrentReviewIndex((prevIndex) =>
            prevIndex === maxIndex ? 0 : prevIndex + 1
        );
    };

    const prevReview = () => {
        const maxIndex = Math.ceil(reviews.length / 3) - 1;
        setCurrentReviewIndex((prevIndex) =>
            prevIndex === 0 ? maxIndex : prevIndex - 1
        );
    };

    const handleContactInputChange = (e) => {
        const { name, value } = e.target;
        setContactForm(prev => ({ ...prev, [name]: value }));
    };

    const handleReviewInputChange = (e) => {
        const { name, value } = e.target;
        setReviewForm(prev => ({ ...prev, [name]: value }));
    };

    const handleRatingClick = (rating) => {
        setReviewForm(prev => ({ ...prev, rating }));
    };

    const handleContactSubmit = async (e) => {
        e.preventDefault();
        setContactSubmitting(true);
        setError('');

        try {
            await apiService.submitContact(contactForm);
            setMessage('Thank you! Your message has been sent successfully. We\'ll get back to you within 24 hours.');
            setContactForm({
                first_name: '',
                last_name: '',
                email: '',
                company: '',
                subject: '',
                message: ''
            });
            setShowContactForm(false);
            setTimeout(() => setMessage(''), 5000);
        } catch (error) {
            setError(error.message || 'Failed to send message. Please try again.');
        } finally {
            setContactSubmitting(false);
        }
    };

    const handleReviewSubmit = async (e) => {
        e.preventDefault();
        setReviewSubmitting(true);
        setError('');

        try {
            await apiService.submitReview({
                name: reviewForm.name.trim(),
                email: reviewForm.email.trim() || null,
                rating: reviewForm.rating,
                review: reviewForm.review.trim()
            });

            setMessage('Thank you! Your review has been submitted successfully.');
            setReviewForm({ name: '', email: '', rating: 5, review: '' });
            setShowReviewForm(false);

            // Refresh reviews
            setTimeout(() => {
                fetchReviews();
                setMessage('');
            }, 2000);

        } catch (error) {
            setError(error.message || 'Failed to submit review. Please try again.');
        } finally {
            setReviewSubmitting(false);
        }
    };

    const renderStars = (rating, interactive = false) => {
        return [...Array(5)].map((_, index) => (
            <FaStar
                key={index}
                className={`star ${index < rating ? 'star-filled' : 'star-empty'} ${interactive ? 'interactive' : ''}`}
                onClick={() => interactive && handleRatingClick(index + 1)}
            />
        ));
    };

    const renderReviews = () => {
        if (reviews.length === 0) return null;

        const startIndex = currentReviewIndex * 3;
        const endIndex = startIndex + 3;
        const currentReviews = reviews.slice(startIndex, endIndex);

        return currentReviews.map((review) => (
            <div key={review.id} className="review-card">
                <div className="review-header">
                    <div className="review-stars">
                        {renderStars(review.rating)}
                    </div>
                    {review.is_verified && (
                        <span className="verified-badge">
                            <FaCheckCircle />
                            Verified
                        </span>
                    )}
                </div>
                <p className="review-text">"{review.review}"</p>
                <div className="review-footer">
                    <span className="reviewer-name">— {review.name}</span>
                    <span className="review-date">
                        {new Date(review.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                        })}
                    </span>
                </div>
            </div>
        ));
    };

    const renderDots = () => {
        if (reviews.length === 0) return null;

        const totalDots = Math.ceil(reviews.length / 3);
        return [...Array(totalDots)].map((_, index) => (
            <button
                key={index}
                className={`dot ${index === currentReviewIndex ? 'active' : ''}`}
                onClick={() => setCurrentReviewIndex(index)}
            />
        ));
    };

    // Page content rendering remains the same for about, terms, etc.
    const renderPageContent = () => {
        switch (currentPage) {
            case 'about':
                return renderAboutPage();
            case 'contact':
                return renderContactPage();
            case 'terms':
                return renderTermsPage();
            case 'reviews':
                return renderReviewsPage();
            default:
                return renderHomePage();
        }
    };

    const renderReviewsPage = () => (
        <div className="page-content">
            <div className="container">
                <div className="content-header">
                    <h1>Customer Reviews</h1>
                    <p className="subtitle">See what our users are saying about Task Prioritizer</p>
                </div>

                <div className="reviews-page-content">
                    <div className="reviews-page-header">
                        <button
                            className="btn btn-primary"
                            onClick={() => setShowReviewForm(true)}
                        >
                            <FaPlus />
                            Share Your Experience
                        </button>
                    </div>

                    <div className="reviews-full-grid">
                        {reviews.length === 0 ? (
                            <div className="empty-reviews">
                                <p>No reviews yet. Be the first to share your experience!</p>
                            </div>
                        ) : (
                            reviews.map((review) => (
                                <div key={review.id} className="review-card">
                                    <div className="review-header">
                                        <div className="review-stars">
                                            {renderStars(review.rating)}
                                        </div>
                                        {review.is_verified && (
                                            <span className="verified-badge">
                                                <FaCheckCircle />
                                                Verified
                                            </span>
                                        )}
                                    </div>
                                    <p className="review-text">"{review.review}"</p>
                                    <div className="review-footer">
                                        <span className="reviewer-name">— {review.name}</span>
                                        <span className="review-date">
                                            {new Date(review.created_at).toLocaleDateString()}
                                        </span>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    const renderHomePage = () => (
        <>
            <section className="reviews-section">
                <div className="hero-background"></div>
                <div className="container">
                    <div className="hero-content">
                        <div className="hero-text">
                            <h1>Task Prioritization</h1>
                            <p className="hero-subtitle">
                                Transform your team's productivity with intelligent task analysis. Get data-driven insights to prioritize work that matters most.
                            </p>
                            <div className="hero-features">
                                <div className="hero-feature">
                                    <FaCheckCircle />
                                    <span>Smart AI Analysis</span>
                                </div>
                                <div className="hero-feature">
                                    <FaCheckCircle />
                                    <span>Instant CSV Import</span>
                                </div>
                                <div className="hero-feature">
                                    <FaCheckCircle />
                                    <span>Visual Insights</span>
                                </div>
                            </div>
                            <div className="cta-buttons">
                                <Link to="/register" className="btn btn-primary">
                                    Get Started
                                </Link>
                                <Link to="/login" className="btn btn-secondary">
                                    Sign In
                                </Link>
                            </div>
                        </div>
                        <div className="hero-image">
                            <div className="dashboard-mockup">
                                <div className="mockup-header">
                                    <div className="mockup-dots">
                                        <span></span>
                                        <span></span>
                                        <span></span>
                                    </div>
                                    <span className="mockup-title">Task Dashboard</span>
                                </div>
                                <div className="mockup-content">
                                    <div className="mockup-task high">
                                        <div className="task-priority"></div>
                                        <div className="task-info">
                                            <div className="task-title">Critical Bug Fix</div>
                                            <div className="task-meta">Priority: High • Est: 4h</div>
                                        </div>
                                    </div>
                                    <div className="mockup-task medium">
                                        <div className="task-priority"></div>
                                        <div className="task-info">
                                            <div className="task-title">Feature Implementation</div>
                                            <div className="task-meta">Priority: Medium • Est: 8h</div>
                                        </div>
                                    </div>
                                    <div className="mockup-task low">
                                        <div className="task-priority"></div>
                                        <div className="task-info">
                                            <div className="task-title">Documentation Update</div>
                                            <div className="task-meta">Priority: Low • Est: 2h</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features section */}
            <section className="reviews-section">
                <div className="container">
                    <div className="section-header">
                        <h2>Why Choose Task Prioritizer?</h2>
                        <p>Powerful features designed to streamline your workflow and boost productivity</p>
                    </div>
                    <div className="feature-grid">
                        <div className="feature-card">
                            <div className="feature-icon">
                                <FaRobot />
                            </div>
                            <h3>Intelligent Analysis</h3>
                            <p>Advanced AI algorithms analyze task descriptions to automatically determine importance, urgency, and complexity with remarkable accuracy.</p>
                        </div>
                        <div className="feature-card">
                            <div className="feature-icon">
                                <FaChartPie />
                            </div>
                            <h3>Visual Insights</h3>
                            <p>Interactive dashboards and charts provide clear visualization of task relationships, priorities, and project timelines at a glance.</p>
                        </div>
                        <div className="feature-card">
                            <div className="feature-icon">
                                <FaTasks />
                            </div>
                            <h3>Bulk Processing</h3>
                            <p>Upload entire project backlogs via CSV and get instant AI-powered prioritization for hundreds of tasks in seconds.</p>
                        </div>
                        <div className="feature-card">
                            <div className="feature-icon">
                                <FaClock />
                            </div>
                            <h3>Time Estimation</h3>
                            <p>Get accurate time estimates based on complexity analysis, helping you plan sprints and allocate resources effectively.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* How it works section */}
            <section className="reviews-section">
                <div className="container">
                    <div className="section-header">
                        <h2>How It Works</h2>
                        <p>Get started in minutes with our simple, powerful workflow</p>
                    </div>
                    <div className="steps-container">
                        <div className="step">
                            <div className="step-icon">
                                <div className="step-number">1</div>
                                <FaTasks />
                            </div>
                            <div className="step-content">
                                <h3>Import Your Tasks</h3>
                                <p>Upload your task list via CSV or create tasks manually. Our system supports all major project management formats.</p>
                            </div>
                        </div>
                        <div className="step-arrow">→</div>
                        <div className="step">
                            <div className="step-icon">
                                <div className="step-number">2</div>
                                <FaRobot />
                            </div>
                            <div className="step-content">
                                <h3>AI Analysis</h3>
                                <p>Our Claude AI engine analyzes each task, considering context, dependencies, and business impact to generate priority scores.</p>
                            </div>
                        </div>
                        <div className="step-arrow">→</div>
                        <div className="step">
                            <div className="step-icon">
                                <div className="step-number">3</div>
                                <FaChartPie />
                            </div>
                            <div className="step-content">
                                <h3>Review Insights</h3>
                                <p>Explore AI-generated recommendations through intuitive dashboards, charts, and detailed priority breakdowns.</p>
                            </div>
                        </div>
                        <div className="step-arrow">→</div>
                        <div className="step">
                            <div className="step-icon">
                                <div className="step-number">4</div>
                                <FaCheckCircle />
                            </div>
                            <div className="step-content">
                                <h3>Execute with Confidence</h3>
                                <p>Make informed decisions based on data-driven insights and watch your team's productivity soar.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Reviews section */}
            <section className="reviews-section">
                <div className="container">
                    <div className="section-header">
                        <h2>Trusted by Teams Worldwide</h2>
                        <p>See what our users are saying about their experience</p>
                    </div>

                    <div className="reviews-carousel">
                        <button className="carousel-btn prev-btn" onClick={prevReview}>
                            <FaChevronLeft />
                        </button>

                        <div className="reviews-container">
                            <div className="reviews-grid">
                                {renderReviews()}
                            </div>
                        </div>

                        <button className="carousel-btn next-btn" onClick={nextReview}>
                            <FaChevronRight />
                        </button>
                    </div>

                    <div className="carousel-dots">
                        {renderDots()}
                    </div>

                    <div className="reviews-cta">
                        <button
                            className="add-review-btn"
                            onClick={() => setShowReviewForm(true)}
                        >
                            <FaPlus />
                            Share Your Experience
                        </button>
                        <Link to="/reviews" className="btn btn-outline">
                            View All Reviews
                        </Link>
                    </div>
                </div>
            </section>
        </>
    );

    // Other page renderers (about, contact, terms) remain the same as in the original code
    const renderAboutPage = () => (
        <div className="page-content">
            <div className="container">
                <div className="content-header">
                    <h1>About Task Prioritizer</h1>
                    <p className="subtitle">Revolutionizing project management through artificial intelligence</p>
                </div>

                <div className="content-grid">
                    <div className="content-section">
                        <h2>Our Mission</h2>
                        <p>We believe that every team deserves to work efficiently and focus on what matters most. Our AI-powered task prioritization platform helps organizations of all sizes make better decisions about their work by providing intelligent insights into task importance, urgency, and complexity.</p>
                    </div>

                    <div className="content-section">
                        <h2>Our Story</h2>
                        <p>Founded by a team of experienced project managers and AI researchers, we recognized the challenge teams face in prioritizing their ever-growing task lists. Traditional methods often rely on gut feeling or simple categorization, but we knew there was a better way.</p>
                        <p>By leveraging advanced AI models like Claude, we've created a system that can understand the nuances of task descriptions, analyze dependencies, and provide data-driven prioritization recommendations that actually make sense.</p>
                    </div>

                    <div className="content-section full-width">
                        <h2>What Sets Us Apart</h2>
                        <div className="features-grid">
                            <div className="feature-item">
                                <div className="feature-item-icon">
                                    <FaRobot />
                                </div>
                                <h3>Advanced AI Technology</h3>
                                <p>Our platform uses state-of-the-art natural language processing to understand task context and business impact.</p>
                            </div>
                            <div className="feature-item">
                                <div className="feature-item-icon">
                                    <FaChartPie />
                                </div>
                                <h3>Data-Driven Insights</h3>
                                <p>Get actionable recommendations based on comprehensive analysis of your project data and team performance.</p>
                            </div>
                            <div className="feature-item">
                                <div className="feature-item-icon">
                                    <FaTasks />
                                </div>
                                <h3>Seamless Integration</h3>
                                <p>Import existing task lists and integrate with popular project management tools without disrupting your workflow.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderContactPage = () => (
        <div className="page-content">
            <div className="container">
                <div className="content-header">
                    <h1>Get in Touch</h1>
                    <p className="subtitle">We're here to help you optimize your workflow</p>
                </div>

                {message && (
                    <div className="alert alert-success">
                        <FaCheckCircle />
                        {message}
                    </div>
                )}

                {error && (
                    <div className="alert alert-error">
                        {error}
                    </div>
                )}

                <div className="contact-grid">
                    <div className="contact-info">
                        <h2>Contact Information</h2>
                        <p>Have questions about our AI task prioritization platform? Our team is ready to help you get started and make the most of our powerful features.</p>

                        <div className="contact-methods">
                            <div className="contact-method">
                                <div className="contact-method-icon">
                                    <FaEnvelope />
                                </div>
                                <div className="contact-method-content">
                                    <h3>Email Support</h3>
                                    <p>wakinniranye@yahoo.com</p>
                                    <span>Response within 24 hours</span>
                                </div>
                            </div>
                            <div className="contact-method">
                                <div className="contact-method-icon">
                                    <FaPhone />
                                </div>
                                <div className="contact-method-content">
                                    <h3>Phone Support</h3>
                                    <p>+447448850674</p>
                                    <span>Mon-Fri, 9AM-6PM PST</span>
                                </div>
                            </div>
                            <div className="contact-method">
                                <div className="contact-method-icon">
                                    <FaMapMarkerAlt />
                                </div>
                                <div className="contact-method-content">
                                    <h3>Headquarters</h3>
                                    <p>74 Great western road, AB10 6QF, <br />Aberdeen, UK</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="contact-form-section">
                        <div className="contact-form">
                            <h2>Send us a Message</h2>
                            <button
                                className="btn btn-primary btn-large"
                                onClick={() => setShowContactForm(true)}
                            >
                                Open Contact Form
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderTermsPage = () => (
        <div className="page-content">
            <div className="container">
                <div className="content-header">
                    <h1>Terms of Service</h1>
                    <p className="subtitle">Last updated: January 15, 2025</p>
                </div>

                <div className="terms-content">
                    <div className="terms-nav">
                        <h3>Table of Contents</h3>
                        <ul>
                            <li><a href="#acceptance">1. Acceptance of Terms</a></li>
                            <li><a href="#service">2. Description of Service</a></li>
                            <li><a href="#accounts">3. User Accounts</a></li>
                            <li><a href="#usage">4. Acceptable Use</a></li>
                            <li><a href="#privacy">5. Data and Privacy</a></li>
                            <li><a href="#intellectual">6. Intellectual Property</a></li>
                            <li><a href="#liability">7. Limitation of Liability</a></li>
                            <li><a href="#termination">8. Termination</a></li>
                            <li><a href="#changes">9. Changes to Terms</a></li>
                            <li><a href="#contact-terms">10. Contact Information</a></li>
                        </ul>
                    </div>

                    <div className="terms-sections">
                        <div className="content-section" id="acceptance">
                            <h2>1. Acceptance of Terms</h2>
                            <p>By accessing and using the AI Task Prioritization platform ("Service"), you accept and agree to be bound by the terms and provisions of this agreement.</p>
                        </div>

                        <div className="content-section" id="service">
                            <h2>2. Description of Service</h2>
                            <p>AI Task Prioritization is a web-based platform that uses artificial intelligence to analyze and prioritize tasks for individuals and teams.</p>
                        </div>

                        {/* Add more sections as needed */}
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="landing-page">
            {/* Navigation Header */}
            <header className="navbar">
                <div className="container">
                    <div className="nav-brand">
                        <button onClick={() => setCurrentPage('home')}>
                            <FaRobot className="brand-icon" />
                            <span>Task Prioritizer</span>
                        </button>
                    </div>
                    <nav className="nav-menu">
                        <div className="nav-links">
                            <button
                                className={currentPage === 'home' ? 'nav-link active' : 'nav-link'}
                                onClick={() => setCurrentPage('home')}
                            >
                                Home
                            </button>
                            <button
                                className={currentPage === 'about' ? 'nav-link active' : 'nav-link'}
                                onClick={() => setCurrentPage('about')}
                            >
                                About
                            </button>
                            <button
                                className={currentPage === 'reviews' ? 'nav-link active' : 'nav-link'}
                                onClick={() => setCurrentPage('reviews')}
                            >
                                Reviews
                            </button>
                            <button
                                className={currentPage === 'contact' ? 'nav-link active' : 'nav-link'}
                                onClick={() => setCurrentPage('contact')}
                            >
                                Contact
                            </button>
                        </div>
                        <div className="nav-actions">
                            <Link to="/login" className="btn btn-ghost">Sign In</Link>
                            <Link to="/register" className="btn btn-primary">Get Started</Link>
                        </div>
                    </nav>
                </div>
            </header>

            {/* Main Content Wrapper */}
            <main className="main-content">
                {renderPageContent()}
            </main>

            {/* Contact Form Modal */}
            {showContactForm && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h2>Contact Us</h2>
                            <button
                                className="close-btn"
                                onClick={() => setShowContactForm(false)}
                            >
                                <FaTimes />
                            </button>
                        </div>

                        <form onSubmit={handleContactSubmit} className="contact-modal-form">
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="first_name">First Name *</label>
                                    <input
                                        type="text"
                                        id="first_name"
                                        name="first_name"
                                        value={contactForm.first_name}
                                        onChange={handleContactInputChange}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="last_name">Last Name *</label>
                                    <input
                                        type="text"
                                        id="last_name"
                                        name="last_name"
                                        value={contactForm.last_name}
                                        onChange={handleContactInputChange}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="email">Email Address *</label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={contactForm.email}
                                    onChange={handleContactInputChange}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="company">Company</label>
                                <input
                                    type="text"
                                    id="company"
                                    name="company"
                                    value={contactForm.company}
                                    onChange={handleContactInputChange}
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="subject">Subject *</label>
                                <input
                                    type="text"
                                    id="subject"
                                    name="subject"
                                    value={contactForm.subject}
                                    onChange={handleContactInputChange}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="message">Message *</label>
                                <textarea
                                    id="message"
                                    name="message"
                                    value={contactForm.message}
                                    onChange={handleContactInputChange}
                                    required
                                    rows="6"
                                    placeholder="Tell us about your question or feedback..."
                                    minLength="20"
                                />
                            </div>

                            <div className="form-actions">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowContactForm(false)}
                                    disabled={contactSubmitting}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={contactSubmitting}
                                >
                                    {contactSubmitting ? (
                                        <>
                                            <FaSpinner className="spinner" />
                                            Sending...
                                        </>
                                    ) : (
                                        'Send Message'
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Review Form Modal */}
            {showReviewForm && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h2>Add Your Review</h2>
                            <button
                                className="close-btn"
                                onClick={() => setShowReviewForm(false)}
                            >
                                <FaTimes />
                            </button>
                        </div>

                        <form onSubmit={handleReviewSubmit} className="review-modal-form">
                            <div className="form-group">
                                <label htmlFor="review_name">Name *</label>
                                <input
                                    type="text"
                                    id="review_name"
                                    name="name"
                                    value={reviewForm.name}
                                    onChange={handleReviewInputChange}
                                    required
                                    placeholder="Your name"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="review_email">Email (optional)</label>
                                <input
                                    type="email"
                                    id="review_email"
                                    name="email"
                                    value={reviewForm.email}
                                    onChange={handleReviewInputChange}
                                    placeholder="your@email.com"
                                />
                            </div>

                            <div className="form-group">
                                <label>Rating *</label>
                                <div className="rating-input">
                                    {renderStars(reviewForm.rating, true)}
                                    <span className="rating-text">({reviewForm.rating} star{reviewForm.rating !== 1 ? 's' : ''})</span>
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="review_text">Review *</label>
                                <textarea
                                    id="review_text"
                                    name="review"
                                    value={reviewForm.review}
                                    onChange={handleReviewInputChange}
                                    required
                                    rows="4"
                                    placeholder="Tell us about your experience with Task Prioritizer..."
                                    minLength="10"
                                />
                                <small className="character-count">
                                    {reviewForm.review.length}/10 minimum characters
                                </small>
                            </div>

                            <div className="form-actions">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowReviewForm(false)}
                                    disabled={reviewSubmitting}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={reviewSubmitting}
                                >
                                    {reviewSubmitting ? (
                                        <>
                                            <FaSpinner className="spinner" />
                                            Submitting...
                                        </>
                                    ) : (
                                        'Submit Review'
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Global Messages */}
            {message && (
                <div className="floating-message success">
                    <FaCheckCircle />
                    {message}
                </div>
            )}

            {error && (
                <div className="floating-message error">
                    {error}
                </div>
            )}

            {/* Footer */}
            <footer className="footer">
                <div className="container">
                    <div className="footer-content">
                        <div className="footer-main">
                            <div className="footer-brand">
                                <div className="brand">
                                    <FaRobot className="brand-icon" />
                                    <span>Task Prioritizer</span>
                                </div>
                                <p>Revolutionizing project management through artificial intelligence. Make smarter decisions about your work with our AI-powered prioritization platform.</p>
                                <div className="social-links">
                                    <a href="#" aria-label="LinkedIn"><FaLinkedin /></a>
                                    <a href="#" aria-label="Twitter"><FaTwitter /></a>
                                    <a href="#" aria-label="GitHub"><FaGithub /></a>
                                </div>
                            </div>
                        </div>

                        <div className="footer-links">
                            <div className="footer-section">
                                <h3>Product</h3>
                                <ul>
                                    <li><button onClick={() => setCurrentPage('home')}>Features</button></li>
                                    <li><button onClick={() => setCurrentPage('reviews')}>Reviews</button></li>
                                </ul>
                            </div>

                            <div className="footer-section">
                                <h3>Company</h3>
                                <ul>
                                    <li><button onClick={() => setCurrentPage('about')}>About Us</button></li>
                                </ul>
                            </div>

                            <div className="footer-section">
                                <h3>Support</h3>
                                <ul>
                                    <li><button onClick={() => setCurrentPage('contact')}>Contact Us</button></li>
                                </ul>
                            </div>

                            <div className="footer-section">
                                <h3>Legal</h3>
                                <ul>
                                    <li><button onClick={() => setCurrentPage('terms')}>Terms of Service</button></li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div className="footer-bottom">
                        <div className="footer-bottom-content">
                            <p>&copy; 2025 Akinwale Akinniranye. All rights reserved.</p>
                            <div className="footer-bottom-links">
                                <button onClick={() => setCurrentPage('terms')}>Terms</button>
                                <a href="#">Privacy</a>
                                <a href="#">Cookies</a>
                            </div>
                        </div>
                    </div>
                </div>
            </footer>

            {/* Updated Styles */}
            <style jsx>{`
                * {
                    margin: 0;
                    padding: 0;
                    box-sizing: border-box;
                }

                html, body {
                    margin: 0;
                    padding: 0;
                    height: 100%;
                }

                .landing-page {
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                    color: #1a202c;
                    line-height: 1.6;
                    background: #ffffff;
                    min-height: 100vh;
                    display: flex;
                    flex-direction: column;
                    margin: 0;
                    padding: 0;
                }

                .main-content {
                    flex: 1;
                    margin: 0;
                    padding: 0;
                }

                .container {
                    max-width: 1200px;
                    margin: 0 auto;
                    padding: 0 24px;
                }

                /* Alerts */
                .alert {
                    padding: 16px 20px;
                    border-radius: 8px;
                    margin-bottom: 24px;
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    font-weight: 500;
                }

                .alert-success {
                    background: #d1fae5;
                    color: #065f46;
                    border: 1px solid #a7f3d0;
                }

                .alert-error {
                    background: #fee2e2;
                    color: #991b1b;
                    border: 1px solid #fca5a5;
                }

                /* Floating Messages */
                .floating-message {
                    position: fixed;
                    top: 100px;
                    right: 24px;
                    padding: 16px 20px;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    font-weight: 500;
                    z-index: 2000;
                    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
                    max-width: 400px;
                }

                .floating-message.success {
                    background: #d1fae5;
                    color: #065f46;
                    border: 1px solid #a7f3d0;
                }

                .floating-message.error {
                    background: #fee2e2;
                    color: #991b1b;
                    border: 1px solid #fca5a5;
                }

                /* Modal Styles */
                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                    padding: 20px;
                }

                .modal-content {
                    background: white;
                    border-radius: 16px;
                    width: 100%;
                    max-width: 500px;
                    max-height: 90vh;
                    overflow-y: auto;
                    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
                }

                .modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 24px 24px 0 24px;
                    margin-bottom: 24px;
                }

                .modal-header h2 {
                    font-size: 24px;
                    font-weight: 700;
                    color: #1a202c;
                    margin: 0;
                }

                .close-btn {
                    background: none;
                    border: none;
                    font-size: 18px;
                    color: #64748b;
                    cursor: pointer;
                    padding: 8px;
                    border-radius: 4px;
                    transition: all 0.2s ease;
                }

                .close-btn:hover {
                    background: #f1f5f9;
                    color: #1a202c;
                }

                .contact-modal-form,
                .review-modal-form {
                    padding: 0 24px 24px 24px;
                }

                .form-row {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 16px;
                }

                .form-group {
                    margin-bottom: 20px;
                }

                .form-group label {
                    display: block;
                    margin-bottom: 8px;
                    font-weight: 600;
                    color: #374151;
                    font-size: 14px;
                }

                .form-group input,
                .form-group textarea {
                    width: 100%;
                    padding: 12px 16px;
                    border: 1px solid #d1d5db;
                    border-radius: 8px;
                    font-size: 14px;
                    transition: all 0.3s ease;
                    font-family: inherit;
                }

                .form-group input:focus,
                .form-group textarea:focus {
                    outline: none;
                    border-color: #2563eb;
                    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
                }

                .rating-input {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    margin-top: 8px;
                }

                .star {
                    font-size: 20px;
                    transition: all 0.2s ease;
                }

                .star-filled {
                    color: #f59e0b;
                }

                .star-empty {
                    color: #e5e7eb;
                }

                .star.interactive {
                    cursor: pointer;
                    font-size: 24px;
                }

                .star.interactive:hover {
                    transform: scale(1.1);
                }

                .rating-text {
                    font-size: 14px;
                    color: #64748b;
                    font-weight: 500;
                }

                .character-count {
                    display: block;
                    margin-top: 4px;
                    font-size: 12px;
                    color: #64748b;
                }

                .form-actions {
                    display: flex;
                    justify-content: flex-end;
                    gap: 12px;
                    margin-top: 32px;
                    padding-top: 20px;
                    border-top: 1px solid #e5e7eb;
                }

                .spinner {
                    animation: spin 1s linear infinite;
                }

                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }

                /* Reviews Page Specific Styles */
                .reviews-page-content {
                    padding: 60px 0;
                }

                .reviews-page-header {
                    text-align: center;
                    margin-bottom: 40px;
                }

                .reviews-full-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
                    gap: 24px;
                }

                .empty-reviews {
                    grid-column: 1 / -1;
                    text-align: center;
                    padding: 80px 20px;
                    background: white;
                    border-radius: 16px;
                    border: 1px solid #e5e7eb;
                }

                .reviews-cta {
                    text-align: center;
                    margin-top: 40px;
                    display: flex;
                    justify-content: center;
                    gap: 16px;
                    flex-wrap: wrap;
                }

                /* Keep all existing styles from the original component */
                .navbar {
                    background: rgba(255, 255, 255, 0.95);
                    backdrop-filter: blur(10px);
                    border-bottom: 1px solid #e2e8f0;
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    z-index: 1000;
                    transition: all 0.3s ease;
                    margin: 0;
                    padding: 0;
                }

                .navbar .container {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 16px 24px;
                    margin: 0 auto;
                }

                .nav-brand button {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    background: none;
                    border: none;
                    cursor: pointer;
                    font-size: 20px;
                    font-weight: 700;
                    color: #2563eb;
                    transition: all 0.3s ease;
                }

                .nav-brand button:hover {
                    color: #1d4ed8;
                }

                .brand-icon {
                    font-size: 24px;
                }

                .nav-menu {
                    display: flex;
                    align-items: center;
                    gap: 32px;
                }

                .nav-links {
                    display: flex;
                    align-items: center;
                    gap: 32px;
                }

                .nav-link {
                    background: none;
                    border: none;
                    color: #64748b;
                    font-weight: 500;
                    font-size: 15px;
                    cursor: pointer;
                    padding: 8px 0;
                    position: relative;
                    transition: all 0.3s ease;
                }

                .nav-link:hover,
                .nav-link.active {
                    color: #2563eb;
                }

                .nav-link.active::after {
                    content: '';
                    position: absolute;
                    bottom: -16px;
                    left: 0;
                    right: 0;
                    height: 2px;
                    background: #2563eb;
                }

                .nav-actions {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                }

                .btn {
                    padding: 10px 20px;
                    border-radius: 8px;
                    font-weight: 600;
                    font-size: 14px;
                    text-decoration: none;
                    border: none;
                    cursor: pointer;
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    transition: all 0.3s ease;
                    white-space: nowrap;
                }

                .btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .btn-primary {
                    background: #2563eb;
                    color: white;
                    box-shadow: 0 1px 3px rgba(37, 99, 235, 0.12);
                }

                .btn-primary:hover:not(:disabled) {
                    background: #1d4ed8;
                    box-shadow: 0 4px 6px rgba(37, 99, 235, 0.25);
                    transform: translateY(-1px);
                }

                .btn-secondary {
                    background: white;
                    color: #2563eb;
                    border: 1px solid #e2e8f0;
                }

                .btn-secondary:hover:not(:disabled) {
                    background: #f8fafc;
                    border-color: #2563eb;
                }

                .btn-outline {
                    background: transparent;
                    color: #2563eb;
                    border: 1px solid #2563eb;
                }

                .btn-outline:hover {
                    background: #2563eb;
                    color: white;
                }

                .btn-ghost {
                    background: transparent;
                    color: #64748b;
                }

                .btn-ghost:hover {
                    color: #2563eb;
                    background: #f1f5f9;
                }

                .btn-large {
                    padding: 14px 28px;
                    font-size: 16px;
                }

                /* Hero Section */
                .hero {
                    padding: 120px 0 80px;
                    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
                    position: relative;
                    overflow: hidden;
                    margin: 80px 0 0 0;
                }

                .hero-background {
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: 
                        radial-gradient(circle at 25% 25%, rgba(37, 99, 235, 0.03) 0%, transparent 50%),
                        radial-gradient(circle at 75% 75%, rgba(16, 185, 129, 0.03) 0%, transparent 50%);
                }

                .hero-content {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 80px;
                    align-items: center;
                    position: relative;
                }

                .hero-text h1 {
                    font-size: 48px;
                    font-weight: 800;
                    line-height: 1.1;
                    color: #1a202c;
                    margin-bottom: 24px;
                    letter-spacing: -0.025em;
                }

                .hero-subtitle {
                    font-size: 20px;
                    color: #64748b;
                    margin-bottom: 32px;
                    line-height: 1.6;
                }

                .hero-features {
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                    margin-bottom: 40px;
                }

                .hero-feature {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    color: #475569;
                    font-weight: 500;
                }

                .hero-feature svg {
                    color: #10b981;
                    font-size: 16px;
                }

                .cta-buttons {
                    display: flex;
                    gap: 16px;
                    flex-wrap: wrap;
                }

                /* Dashboard Mockup */
                .hero-image {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                }

                .dashboard-mockup {
                    width: 100%;
                    max-width: 480px;
                    background: white;
                    border-radius: 16px;
                    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
                    overflow: hidden;
                    border: 1px solid #e2e8f0;
                }

                .mockup-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 16px 20px;
                    background: #f8fafc;
                    border-bottom: 1px solid #e2e8f0;
                }

                .mockup-dots {
                    display: flex;
                    gap: 6px;
                }

                .mockup-dots span {
                    width: 8px;
                    height: 8px;
                    border-radius: 50%;
                    background: #cbd5e0;
                }

                .mockup-dots span:nth-child(1) { background: #ef4444; }
                .mockup-dots span:nth-child(2) { background: #f59e0b; }
                .mockup-dots span:nth-child(3) { background: #10b981; }

                .mockup-title {
                    font-size: 14px;
                    font-weight: 600;
                    color: #64748b;
                }

                .mockup-content {
                    padding: 24px;
                }

                .mockup-task {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    padding: 16px;
                    margin-bottom: 12px;
                    background: #f8fafc;
                    border-radius: 8px;
                    border-left: 4px solid;
                }

                .mockup-task.high {
                    border-left-color: #ef4444;
                }

                .mockup-task.medium {
                    border-left-color: #f59e0b;
                }

                .mockup-task.low {
                    border-left-color: #10b981;
                }

                .task-priority {
                    width: 8px;
                    height: 8px;
                    border-radius: 50%;
                }

                .mockup-task.high .task-priority {
                    background: #ef4444;
                }

                .mockup-task.medium .task-priority {
                    background: #f59e0b;
                }

                .mockup-task.low .task-priority {
                    background: #10b981;
                }

                .task-title {
                    font-weight: 600;
                    color: #1a202c;
                    font-size: 14px;
                    margin-bottom: 4px;
                }

                .task-meta {
                    font-size: 12px;
                    color: #64748b;
                }

                /* Sections */
                .section-header {
                    text-align: center;
                    margin-bottom: 80px;
                }

                .section-header h2 {
                    font-size: 40px;
                    font-weight: 800;
                    color: #1a202c;
                    margin-bottom: 16px;
                    letter-spacing: -0.025em;
                }

                .section-header p {
                    font-size: 18px;
                    color: #64748b;
                    max-width: 600px;
                    margin: 0 auto;
                }

                /* Reviews Section */
                .reviews-section {
                    padding: 100px 0;
                    background: white;
                    margin: 0;
                }

                .feature-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
                    gap: 40px;
                }

                .feature-card {
                    background: white;
                    padding: 40px 32px;
                    border-radius: 16px;
                    border: 1px solid #e2e8f0;
                    transition: all 0.3s ease;
                    position: relative;
                }

                .feature-card:hover {
                    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
                    transform: translateY(-4px);
                    border-color: #cbd5e0;
                }

                .feature-icon {
                    width: 56px;
                    height: 56px;
                    background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    font-size: 24px;
                    margin-bottom: 24px;
                }

                .feature-card h3 {
                    font-size: 20px;
                    font-weight: 700;
                    color: #1a202c;
                    margin-bottom: 16px;
                }

                .feature-card p {
                    color: #64748b;
                    line-height: 1.7;
                }

                /* Steps */
                .steps-container {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 32px;
                    align-items: start;
                    position: relative;
                }

                .steps-container::before {
                    content: '';
                    position: absolute;
                    top: 40px;
                    left: 12.5%;
                    right: 12.5%;
                    height: 2px;
                    background: linear-gradient(90deg, #2563eb, #10b981);
                    z-index: 0;
                }

                .step {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    text-align: center;
                    position: relative;
                    z-index: 1;
                }

                .step-icon {
                    position: relative;
                    width: 80px;
                    height: 80px;
                    background: white;
                    border: 3px solid #2563eb;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin-bottom: 24px;
                    font-size: 24px;
                    color: #2563eb;
                    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
                }

                .step-number {
                    position: absolute;
                    top: -8px;
                    right: -8px;
                    width: 24px;
                    height: 24px;
                    background: #2563eb;
                    color: white;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 12px;
                    font-weight: 700;
                    box-shadow: 0 2px 4px rgba(37, 99, 235, 0.4);
                }

                .step-content h3 {
                    font-size: 18px;
                    font-weight: 700;
                    color: #1a202c;
                    margin-bottom: 12px;
                }

                .step-content p {
                    color: #64748b;
                    font-size: 14px;
                    line-height: 1.6;
                    max-width: 250px;
                }

                .step-arrow {
                    display: none;
                }

                .reviews-carousel {
                    position: relative;
                    display: flex;
                    align-items: center;
                    gap: 24px;
                    margin-bottom: 40px;
                }

                .carousel-btn {
                    background: white;
                    border: 1px solid #e2e8f0;
                    color: #64748b;
                    width: 48px;
                    height: 48px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    font-size: 16px;
                    flex-shrink: 0;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
                }

                .carousel-btn:hover {
                    background: #2563eb;
                    color: white;
                    border-color: #2563eb;
                    box-shadow: 0 4px 6px rgba(37, 99, 235, 0.25);
                }

                .reviews-container {
                    flex: 1;
                    overflow: hidden;
                }

                .reviews-grid {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 32px;
                }

                .review-card {
                    background: white;
                    padding: 32px;
                    border-radius: 16px;
                    border: 1px solid #e2e8f0;
                    transition: all 0.3s ease;
                    position: relative;
                }

                .review-card:hover {
                    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
                    transform: translateY(-2px);
                }

                .review-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 20px;
                }

                .review-stars {
                    display: flex;
                    gap: 4px;
                }

                .review-stars .star {
                    font-size: 14px;
                }

                .verified-badge {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    background: #d1fae5;
                    color: #065f46;
                    padding: 4px 12px;
                    border-radius: 12px;
                    font-size: 12px;
                    font-weight: 600;
                }

                .verified-badge svg {
                    font-size: 12px;
                }

                .review-text {
                    font-size: 15px;
                    line-height: 1.7;
                    color: #475569;
                    margin-bottom: 24px;
                    font-style: italic;
                }

                .review-footer {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    font-size: 13px;
                }

                .reviewer-name {
                    font-weight: 600;
                    color: #1a202c;
                }

                .review-date {
                    color: #94a3b8;
                }

                .carousel-dots {
                    display: flex;
                    justify-content: center;
                    gap: 8px;
                    margin-bottom: 40px;
                }

                .dot {
                    width: 8px;
                    height: 8px;
                    border-radius: 50%;
                    border: none;
                    background: #cbd5e0;
                    cursor: pointer;
                    transition: all 0.3s ease;
                }

                .dot.active {
                    background: #2563eb;
                    transform: scale(1.25);
                }

                .add-review-btn {
                    background: #f8fafc;
                    color: #2563eb;
                    border: 1px solid #e2e8f0;
                    padding: 12px 24px;
                    border-radius: 8px;
                    font-weight: 600;
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    font-size: 14px;
                }

                .add-review-btn:hover {
                    background: #2563eb;
                    color: white;
                    border-color: #2563eb;
                    box-shadow: 0 4px 6px rgba(37, 99, 235, 0.25);
                }

                /* Content Pages */
                .page-content {
                    margin: 80px 0 0 0;
                    min-height: calc(100vh - 80px);
                }

                .content-header {
                    text-align: center;
                    padding: 80px 0 60px;
                    background: #f8fafc;
                    margin: 0;
                }

                .content-header h1 {
                    font-size: 48px;
                    font-weight: 800;
                    color: #1a202c;
                    margin-bottom: 16px;
                    letter-spacing: -0.025em;
                }

                .subtitle {
                    font-size: 20px;
                    color: #64748b;
                }

                .content-grid {
                    padding: 80px 0;
                    margin: 0;
                }

                .content-section {
                    margin-bottom: 60px;
                }

                .content-section h2 {
                    font-size: 32px;
                    font-weight: 700;
                    color: #1a202c;
                    margin-bottom: 24px;
                }

                .content-section p {
                    font-size: 16px;
                    line-height: 1.7;
                    color: #64748b;
                    margin-bottom: 16px;
                }

                .features-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
                    gap: 32px;
                    margin-top: 40px;
                }

                .feature-item {
                    background: white;
                    padding: 32px;
                    border-radius: 12px;
                    border: 1px solid #e2e8f0;
                    transition: all 0.3s ease;
                }

                .feature-item:hover {
                    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
                    transform: translateY(-2px);
                }

                .feature-item-icon {
                    width: 48px;
                    height: 48px;
                    background: #dbeafe;
                    color: #2563eb;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 20px;
                    margin-bottom: 20px;
                }

                .feature-item h3 {
                    color: #1a202c;
                    margin-bottom: 12px;
                    font-size: 18px;
                }

                .feature-item p {
                    margin-bottom: 0;
                    font-size: 15px;
                }

                /* Contact Page */
                .contact-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 80px;
                    padding: 80px 0;
                    margin: 0;
                }

                .contact-info h2 {
                    font-size: 32px;
                    font-weight: 700;
                    color: #1a202c;
                    margin-bottom: 20px;
                }

                .contact-info p {
                    font-size: 16px;
                    color: #64748b;
                    line-height: 1.7;
                    margin-bottom: 40px;
                }

                .contact-methods {
                    display: flex;
                    flex-direction: column;
                    gap: 32px;
                }

                .contact-method {
                    display: flex;
                    gap: 20px;
                }

                .contact-method-icon {
                    width: 48px;
                    height: 48px;
                    background: #dbeafe;
                    color: #2563eb;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 20px;
                    flex-shrink: 0;
                }

                .contact-method-content h3 {
                    font-size: 18px;
                    font-weight: 600;
                    color: #1a202c;
                    margin-bottom: 8px;
                }

                .contact-method-content p {
                    font-size: 16px;
                    color: #374151;
                    margin-bottom: 4px;
                }

                .contact-method-content span {
                    font-size: 14px;
                    color: #64748b;
                }

                .contact-form-section {
                    background: #f8fafc;
                    border-radius: 16px;
                    padding: 40px;
                    border: 1px solid #e2e8f0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    text-align: center;
                }

                .contact-form h2 {
                    font-size: 28px;
                    font-weight: 700;
                    color: #1a202c;
                    margin-bottom: 32px;
                }

                /* Terms Page */
                .terms-content {
                    display: grid;
                    grid-template-columns: 250px 1fr;
                    gap: 80px;
                    padding: 80px 0;
                    margin: 0;
                }

                .terms-nav {
                    position: sticky;
                    top: 120px;
                    height: fit-content;
                }

                .terms-nav h3 {
                    font-size: 18px;
                    font-weight: 600;
                    color: #1a202c;
                    margin-bottom: 16px;
                }

                .terms-nav ul {
                    list-style: none;
                    padding: 0;
                }

                .terms-nav li {
                    margin-bottom: 8px;
                }

                .terms-nav a {
                    color: #64748b;
                    text-decoration: none;
                    font-size: 14px;
                    transition: color 0.3s ease;
                }

                .terms-nav a:hover {
                    color: #2563eb;
                }

                .terms-sections .content-section {
                    scroll-margin-top: 120px;
                }

                .terms-sections h2 {
                    font-size: 24px;
                    font-weight: 700;
                    color: #1a202c;
                    margin-bottom: 20px;
                }

                /* Footer */
                .footer {
                    background: #0f172a;
                    color: white;
                    padding: 80px 0 0;
                    margin: 0;
                }

                .footer-content {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 80px;
                    margin-bottom: 60px;
                }

                .footer-main {
                    max-width: 400px;
                }

                .footer-brand .brand {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    font-size: 20px;
                    font-weight: 700;
                    color: white;
                    margin-bottom: 20px;
                }

                .footer-brand .brand-icon {
                    color: #2563eb;
                    font-size: 24px;
                }

                .footer-brand p {
                    color: #94a3b8;
                    line-height: 1.7;
                    margin-bottom: 32px;
                    font-size: 15px;
                }

                .social-links {
                    display: flex;
                    gap: 16px;
                }

                .social-links a {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 40px;
                    height: 40px;
                    background: #1e293b;
                    color: #94a3b8;
                    border-radius: 8px;
                    transition: all 0.3s ease;
                    font-size: 18px;
                }

                .social-links a:hover {
                    background: #2563eb;
                    color: white;
                    transform: translateY(-2px);
                }

                .footer-links {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 40px;
                }

                .footer-section h3 {
                    font-size: 16px;
                    font-weight: 600;
                    color: white;
                    margin-bottom: 20px;
                }

                .footer-section ul {
                    list-style: none;
                    padding: 0;
                }

                .footer-section li {
                    margin-bottom: 12px;
                }

                .footer-section a,
                .footer-section button {
                    color: #94a3b8;
                    text-decoration: none;
                    font-size: 14px;
                    transition: color 0.3s ease;
                    background: none;
                    border: none;
                    cursor: pointer;
                    padding: 0;
                    text-align: left;
                }

                .footer-section a:hover,
                .footer-section button:hover {
                    color: white;
                }

                .footer-bottom {
                    border-top: 1px solid #1e293b;
                    padding: 32px 0;
                    margin: 0;
                }

                .footer-bottom-content {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .footer-bottom p {
                    color: #64748b;
                    font-size: 14px;
                    margin: 0;
                }

                .footer-bottom-links {
                    display: flex;
                    gap: 32px;
                }

                .footer-bottom-links a,
                .footer-bottom-links button {
                    color: #64748b;
                    text-decoration: none;
                    font-size: 14px;
                    background: none;
                    border: none;
                    cursor: pointer;
                    transition: color 0.3s ease;
                }

                .footer-bottom-links a:hover,
                .footer-bottom-links button:hover {
                    color: white;
                }

                /* Mobile Responsive */
                @media (max-width: 768px) {
                    .container {
                        padding: 0 20px;
                    }

                    .navbar .container {
                        flex-direction: column;
                        gap: 16px;
                        padding: 16px 20px;
                    }

                    .nav-menu {
                        width: 100%;
                        justify-content: space-between;
                    }

                    .nav-links {
                        gap: 20px;
                    }

                    .nav-actions {
                        gap: 12px;
                    }

                    .hero {
                        padding: 100px 0 60px;
                    }

                    .hero-content {
                        grid-template-columns: 1fr;
                        gap: 60px;
                        text-align: center;
                    }

                    .hero-text h1 {
                        font-size: 36px;
                    }

                    .hero-subtitle {
                        font-size: 18px;
                    }

                    .section-header h2 {
                        font-size: 32px;
                    }

                    .feature-grid {
                        grid-template-columns: 1fr;
                        gap: 32px;
                    }

                    .steps-container {
                        grid-template-columns: 1fr;
                        gap: 32px;
                    }

                    .steps-container::before {
                        display: none;
                    }

                    .reviews-carousel {
                        margin: 0 -20px 40px -20px;
                        padding: 0 20px;
                    }

                    .carousel-btn {
                        display: none;
                    }

                    .reviews-grid {
                        grid-template-columns: 1fr;
                        gap: 24px;
                    }

                    .reviews-full-grid {
                        grid-template-columns: 1fr;
                    }

                    .contact-grid {
                        grid-template-columns: 1fr;
                        gap: 60px;
                    }

                    .terms-content {
                        grid-template-columns: 1fr;
                        gap: 40px;
                    }

                    .terms-nav {
                        position: static;
                    }

                    .footer-content {
                        grid-template-columns: 1fr;
                        gap: 60px;
                    }

                    .footer-links {
                        grid-template-columns: repeat(2, 1fr);
                        gap: 40px 20px;
                    }

                    .footer-bottom-content {
                        flex-direction: column;
                        gap: 20px;
                        text-align: center;
                    }

                    .form-row {
                        grid-template-columns: 1fr;
                    }

                    .form-actions {
                        flex-direction: column;
                    }

                    .reviews-cta {
                        flex-direction: column;
                        align-items: center;
                    }
                }

                @media (max-width: 480px) {
                    .container {
                        padding: 0 16px;
                    }

                    .hero-text h1 {
                        font-size: 28px;
                    }

                    .hero-subtitle {
                        font-size: 16px;
                    }

                    .section-header h2 {
                        font-size: 28px;
                    }

                    .content-header h1 {
                        font-size: 32px;
                    }

                    .footer-links {
                        grid-template-columns: 1fr;
                        gap: 32px;
                    }
                }

                /* Animation */
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }

                /* Focus styles for accessibility */
                .btn:focus-visible,
                .nav-link:focus-visible,
                .carousel-btn:focus-visible,
                .dot:focus-visible {
                    outline: 2px solid #2563eb;
                    outline-offset: 2px;
                }

                .form-group input:focus-visible,
                .form-group textarea:focus-visible {
                    outline: 2px solid #2563eb;
                    outline-offset: -2px;
                }
            `}</style>
        </div>
    );
};

export default LandingPage;